import { useState, useEffect, useCallback, useRef } from "react";
import { cn } from "@apex/shared";

interface ImageLightboxProps {
  images: string[];
  initialIndex: number;
  alt: string;
  onClose: () => void;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.5;
const DOUBLE_CLICK_ZOOM = 2.5;
const WHEEL_SENSITIVITY = 0.003;
const IDLE_TIMEOUT = 2500;

export function ImageLightbox({ images, initialIndex, alt, onClose }: ImageLightboxProps) {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [showHint, setShowHint] = useState(true);

  const rootRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef({ x: 0, y: 0 });
  const translateStart = useRef({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const pinchStartDistance = useRef(0);
  const pinchStartZoom = useRef(1);
  const idleTimer = useRef<ReturnType<typeof setTimeout>>();

  const hasMultiple = images.length > 1;
  const isZoomed = zoom > 1.01;
  const zoomPercent = Math.round(zoom * 100);

  /* ── Auto-hide controls on idle ────────────────────────── */

  const resetIdleTimer = useCallback(() => {
    setControlsVisible(true);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setControlsVisible(false), IDLE_TIMEOUT);
  }, []);

  useEffect(() => {
    resetIdleTimer();
    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [resetIdleTimer]);

  /* ── Zoom helpers ──────────────────────────────────────── */

  const withTransition = useCallback((fn: () => void) => {
    setIsTransitioning(true);
    fn();
    setTimeout(() => setIsTransitioning(false), 300);
  }, []);

  const resetZoom = useCallback(() => {
    withTransition(() => {
      setZoom(1);
      setTranslate({ x: 0, y: 0 });
    });
  }, [withTransition]);

  const zoomTo = useCallback((newZoom: number, centerX = 0, centerY = 0) => {
    withTransition(() => {
      const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));
      setZoom(clamped);
      if (clamped <= 1) setTranslate({ x: 0, y: 0 });
      else setTranslate({ x: centerX, y: centerY });
    });
  }, [withTransition]);

  const zoomIn = useCallback(() => zoomTo(zoom + ZOOM_STEP), [zoom, zoomTo]);
  const zoomOut = useCallback(() => zoomTo(zoom - ZOOM_STEP), [zoom, zoomTo]);

  /* ── Image navigation ──────────────────────────────────── */

  const changeImage = useCallback((newIndex: number) => {
    setZoom(1);
    setTranslate({ x: 0, y: 0 });
    setIsTransitioning(false);
    setActiveIndex(newIndex);
  }, []);

  const goNext = useCallback(() => {
    changeImage(activeIndex === images.length - 1 ? 0 : activeIndex + 1);
  }, [activeIndex, images.length, changeImage]);

  const goPrev = useCallback(() => {
    changeImage(activeIndex === 0 ? images.length - 1 : activeIndex - 1);
  }, [activeIndex, images.length, changeImage]);

  /* ── Block scroll passthrough ──────────────────────────── */

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const blockWheel = (e: WheelEvent) => e.preventDefault();
    el.addEventListener("wheel", blockWheel, { passive: false });
    const blockTouch = (e: TouchEvent) => {
      if (e.touches.length === 1 && e.target === el) e.preventDefault();
    };
    el.addEventListener("touchmove", blockTouch, { passive: false });
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      el.removeEventListener("wheel", blockWheel);
      el.removeEventListener("touchmove", blockTouch);
      document.body.style.overflow = prev;
    };
  }, []);

  /* ── Auto-dismiss hint ─────────────────────────────────── */

  useEffect(() => {
    const timer = setTimeout(() => setShowHint(false), 3500);
    return () => clearTimeout(timer);
  }, []);

  /* ── Focus management & Tab trap (B13) ─────────────────── */
  // The widget mounts inside `<apex-widget>`'s open shadow root. Inside a
  // shadow root, `document.activeElement` returns the shadow HOST element,
  // not the actually-focused descendant — so reading focus from `document`
  // here would silently capture the host (which is not focusable) and break
  // both the trap and the on-close restore. Resolve the root the lightbox
  // is mounted in and read `activeElement` from there: ShadowRoot when we're
  // inside a shadow tree, Document when we're rendered into the page light
  // DOM (e.g. tests, or any future non-shadow embed).
  function getActiveWithinRoot(node: Node | null): Element | null {
    const root = node?.getRootNode?.();
    if (root && "activeElement" in root) {
      return (root as ShadowRoot | Document).activeElement;
    }
    return document.activeElement;
  }

  useEffect(() => {
    const root = rootRef.current;
    const previouslyFocused = getActiveWithinRoot(root) as HTMLElement | null;
    if (root) root.focus();
    return () => {
      previouslyFocused?.focus?.();
    };
  }, []);

  /* ── Keyboard ──────────────────────────────────────────── */

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      resetIdleTimer();
      if (e.key === "Escape") {
        if (isZoomed) resetZoom();
        else onClose();
      } else if (e.key === "Tab") {
        // Focus trap — keep Tab inside the dialog so keyboard users can't
        // wander to the page behind the modal. Query focusable elements
        // every keystroke because conditional controls (Reset Zoom, nav
        // arrows, thumbnails) come and go as state changes.
        const root = rootRef.current;
        if (!root) return;
        const focusable = Array.from(
          root.querySelectorAll<HTMLElement>(
            "a[href], button:not([disabled]), [tabindex]:not([tabindex='-1'])"
          )
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = getActiveWithinRoot(root) as HTMLElement | null;
        if (e.shiftKey) {
          if (active === first || !root.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (active === last || !root.contains(active)) {
            e.preventDefault();
            first.focus();
          }
        }
      } else if (e.key === "ArrowLeft" && hasMultiple && !isZoomed) {
        goPrev();
      } else if (e.key === "ArrowRight" && hasMultiple && !isZoomed) {
        goNext();
      } else if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        zoomIn();
      } else if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        zoomOut();
      } else if (e.key === "0") {
        resetZoom();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, hasMultiple, goNext, goPrev, isZoomed, resetZoom, zoomIn, zoomOut, resetIdleTimer]);

  /* ── Wheel zoom (cursor-centered) ──────────────────────── */

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.stopPropagation();
    setShowHint(false);
    resetIdleTimer();

    const delta = -e.deltaY * WHEEL_SENSITIVITY;
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom * (1 + delta)));

    if (newZoom <= 1.01) {
      setZoom(1);
      setTranslate({ x: 0, y: 0 });
      return;
    }

    const img = imageRef.current;
    if (img) {
      const rect = img.getBoundingClientRect();
      const cursorX = e.clientX - rect.left - rect.width / 2;
      const cursorY = e.clientY - rect.top - rect.height / 2;
      const scaleFactor = newZoom / zoom;
      setTranslate(prev => ({
        x: cursorX - scaleFactor * (cursorX - prev.x),
        y: cursorY - scaleFactor * (cursorY - prev.y),
      }));
    }
    setZoom(newZoom);
  }, [zoom, resetIdleTimer]);

  /* ── Double-click toggle ───────────────────────────────── */

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setShowHint(false);
    resetIdleTimer();

    if (isZoomed) {
      resetZoom();
    } else {
      const img = imageRef.current;
      if (!img) return;
      const rect = img.getBoundingClientRect();
      const clickX = e.clientX - rect.left - rect.width / 2;
      const clickY = e.clientY - rect.top - rect.height / 2;
      zoomTo(DOUBLE_CLICK_ZOOM, -clickX * (DOUBLE_CLICK_ZOOM - 1), -clickY * (DOUBLE_CLICK_ZOOM - 1));
    }
  }, [isZoomed, resetZoom, zoomTo, resetIdleTimer]);

  /* ── Pointer drag (pan) ────────────────────────────────── */

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!isZoomed || e.pointerType === "touch") return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY };
    translateStart.current = { ...translate };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [isZoomed, translate]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setTranslate({
      x: translateStart.current.x + dx,
      y: translateStart.current.y + dy,
    });
  }, [isDragging]);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  /* ── Touch: pinch zoom + single-finger pan ─────────────── */

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchStartDistance.current = Math.hypot(dx, dy);
      pinchStartZoom.current = zoom;
      setShowHint(false);
    }
  }, [zoom]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const distance = Math.hypot(dx, dy);
      const scale = distance / pinchStartDistance.current;
      const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, pinchStartZoom.current * scale));
      setZoom(newZoom);
      if (newZoom <= 1.01) setTranslate({ x: 0, y: 0 });
    } else if (e.touches.length === 1 && isZoomed) {
      if (!isDragging) {
        setIsDragging(true);
        dragStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        translateStart.current = { ...translate };
      } else {
        const dx = e.touches[0].clientX - dragStart.current.x;
        const dy = e.touches[0].clientY - dragStart.current.y;
        setTranslate({
          x: translateStart.current.x + dx,
          y: translateStart.current.y + dy,
        });
      }
    }
  }, [isZoomed, isDragging, translate]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  /* ── Backdrop click ────────────────────────────────────── */

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      if (isZoomed) resetZoom();
      else onClose();
    }
  }, [isZoomed, resetZoom, onClose]);

  const imageCursor = isDragging ? "grabbing" : isZoomed ? "grab" : "zoom-in";

  /* ── Render ────────────────────────────────────────────── */

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
      tabIndex={-1}
      className="apex-fixed apex-inset-0 apex-z-50 apex-animate-fade-in apex-outline-none"
      style={{
        background: "rgba(0,0,0,0.95)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
      }}
      onMouseMove={resetIdleTimer}
    >
      {/* ─── Full-viewport image area ─── */}
      <div
        className="apex-absolute apex-inset-0 apex-flex apex-items-center apex-justify-center"
        style={{ overflow: "hidden", padding: "3rem 3rem 4.5rem" }}
        onClick={handleBackdropClick}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <img
          ref={imageRef}
          key={activeIndex}
          src={images[activeIndex]}
          alt={`${alt} — image ${activeIndex + 1}`}
          className={cn(
            "apex-select-none apex-max-w-full apex-max-h-full apex-object-contain",
            !isZoomed && !isDragging && "apex-animate-scale-in",
          )}
          style={{
            cursor: imageCursor,
            transform: `scale(${zoom}) translate(${translate.x / zoom}px, ${translate.y / zoom}px)`,
            transition: isTransitioning ? "transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)" : "none",
            transformOrigin: "center center",
            willChange: isDragging ? "transform" : "auto",
          }}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={handleDoubleClick}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          draggable={false}
        />
      </div>

      {/* ─── Always-visible: Close button ─── */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="apex-absolute apex-top-4 apex-right-4 apex-rounded-full apex-p-2.5 hover:apex-bg-white/10 apex-transition-all apex-duration-200 apex-text-white/80 hover:apex-text-white apex-z-20"
        aria-label="Close lightbox"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 6 6 18" />
          <path d="m6 6 12 12" />
        </svg>
      </button>

      {/* ─── Always-visible: Bottom controls (zoom toolbar) ─── */}
      <div
        className="apex-absolute apex-bottom-0 apex-inset-x-0 apex-flex apex-flex-col apex-items-center apex-pb-4 apex-gap-3 apex-z-20 apex-pointer-events-none"
      >
        {/* Zoom toolbar */}
        <div
          className="apex-pointer-events-auto apex-flex apex-items-center apex-gap-0.5 apex-rounded-full apex-p-1"
          style={{
            background: "rgba(0,0,0,0.45)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid rgba(255,255,255,0.1)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={zoomOut}
            disabled={zoom <= MIN_ZOOM}
            className="apex-rounded-full apex-w-8 apex-h-8 apex-flex apex-items-center apex-justify-center apex-text-white hover:apex-bg-white/15 apex-transition-colors disabled:apex-opacity-30 disabled:apex-pointer-events-none"
            aria-label="Zoom out"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" x2="16.65" y1="21" y2="16.65" />
              <line x1="8" x2="14" y1="11" y2="11" />
            </svg>
          </button>

          <span
            className="apex-text-xs apex-font-medium apex-tabular-nums apex-text-white apex-select-none"
            style={{ minWidth: "3rem", textAlign: "center" }}
          >
            {zoomPercent}%
          </span>

          <button
            type="button"
            onClick={zoomIn}
            disabled={zoom >= MAX_ZOOM}
            className="apex-rounded-full apex-w-8 apex-h-8 apex-flex apex-items-center apex-justify-center apex-text-white hover:apex-bg-white/15 apex-transition-colors disabled:apex-opacity-30 disabled:apex-pointer-events-none"
            aria-label="Zoom in"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" x2="16.65" y1="21" y2="16.65" />
              <line x1="11" x2="11" y1="8" y2="14" />
              <line x1="8" x2="14" y1="11" y2="11" />
            </svg>
          </button>

          {isZoomed && (
            <>
              <div className="apex-w-px apex-h-5 apex-mx-0.5" style={{ background: "rgba(255,255,255,0.2)" }} />
              <button
                type="button"
                onClick={resetZoom}
                className="apex-rounded-full apex-h-8 apex-px-2.5 apex-flex apex-items-center apex-gap-1 apex-text-xs apex-font-medium apex-text-white hover:apex-bg-white/15 apex-transition-colors"
                aria-label="Reset zoom"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h6v6" />
                  <path d="M9 21H3v-6" />
                  <path d="M21 3l-7 7" />
                  <path d="M3 21l7-7" />
                </svg>
                Fit
              </button>
            </>
          )}
        </div>

        {/* Thumbnail strip — also always visible */}
        {hasMultiple && !isZoomed && (
          <div className="apex-pointer-events-auto apex-flex apex-gap-2 apex-px-4 apex-overflow-x-auto" onClick={(e) => e.stopPropagation()}>
            {images.map((image, index) => (
              <button
                key={index}
                type="button"
                onClick={() => changeImage(index)}
                className={cn(
                  "apex-shrink-0 apex-w-14 apex-h-10 apex-rounded apex-overflow-hidden apex-border-2 apex-transition-all apex-duration-200",
                  index === activeIndex
                    ? "apex-border-white apex-scale-110"
                    : "apex-border-white/20 hover:apex-border-white/50 apex-opacity-60 hover:apex-opacity-100",
                )}
                aria-label={`View image ${index + 1}`}
              >
                <img src={image} alt="" className="apex-h-full apex-w-full apex-object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ─── Auto-hiding controls: nav arrows, counter, gradient scrims ─── */}
      <div
        className={cn(
          "apex-absolute apex-inset-0 apex-pointer-events-none",
          "apex-transition-opacity apex-duration-500",
          controlsVisible ? "apex-opacity-100" : "apex-opacity-0",
        )}
      >
        {/* Top gradient scrim */}
        <div
          className="apex-absolute apex-top-0 apex-inset-x-0 apex-pointer-events-none"
          style={{ height: "110px", background: "linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, transparent 100%)" }}
        />

        {/* Bottom gradient scrim */}
        <div
          className="apex-absolute apex-bottom-0 apex-inset-x-0 apex-pointer-events-none"
          style={{ height: "160px", background: "linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%)" }}
        />

        {/* Image counter */}
        {hasMultiple && (
          <span className="apex-pointer-events-auto apex-absolute apex-top-5 apex-left-1/2 apex--translate-x-1/2 apex-text-sm apex-text-white/80 apex-font-medium apex-tabular-nums apex-z-10">
            {activeIndex + 1}&thinsp;/&thinsp;{images.length}
          </span>
        )}

        {/* Nav arrows */}
        {hasMultiple && !isZoomed && (
          <>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              className="apex-pointer-events-auto apex-absolute apex-left-3 apex-top-1/2 apex--translate-y-1/2 apex-rounded-full apex-p-3 hover:apex-bg-white/10 apex-transition-all apex-duration-200 apex-text-white/70 hover:apex-text-white"
              aria-label="Previous image"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              className="apex-pointer-events-auto apex-absolute apex-right-3 apex-top-1/2 apex--translate-y-1/2 apex-rounded-full apex-p-3 hover:apex-bg-white/10 apex-transition-all apex-duration-200 apex-text-white/70 hover:apex-text-white"
              aria-label="Next image"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* ─── Zoom hint — absolutely positioned, no layout shift ─── */}
      {showHint && !isZoomed && (
        <div
          className="apex-absolute apex-left-1/2 apex--translate-x-1/2 apex-px-4 apex-py-1.5 apex-rounded-full apex-text-xs apex-text-white apex-pointer-events-none apex-z-10"
          style={{
            bottom: hasMultiple ? "6.5rem" : "4.5rem",
            background: "rgba(0,0,0,0.4)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            animation: "apex-fadeIn 0.5s ease 0.8s both, apex-fadeIn 0.5s ease 3s reverse both",
          }}
        >
          Scroll or double-click to zoom
        </div>
      )}
    </div>
  );
}
